import React from 'react';
import { createPortal } from 'react-dom';
import { Button, ConfigProvider, Dropdown } from 'antd';

import ContextBar from '../common/ContextBar';
import RefreshButton from '../common/RefreshButton';
import { DUPLICATE, KEBAB, LIGHTNING } from '../icons';
import CopiedFromNote from './CopiedFromNote';
import IntakePendingTag from './IntakePendingTag';
import DemoTag from './DemoTag';
import ItemSourceLine from './ItemSourceLine';
import { isDemoItem } from '../../services/records';

/**
 * The item form's head, on its two hosts (moved out of ItemForm by KPR-1644).
 *
 * On the item page it is the context bar as it always was: the collection
 * shell above draws the crumb, the collection badge and the tabs; this is the
 * ITEM's own badge, the card name over the item's title. Action grammar: one
 * primary (Save), labeled secondaries, one ⋯ menu; the autosaved-draft note
 * rides along, and Refresh leads (standards §3; KPR-1346).
 *
 * In the list's detail pane (`paneSlot`, a node in the pane's title row) the
 * pane already names the item, so the form adds only what is its own: the
 * card's action buttons that apply now (KPR-1342) and the form's ⋯ (its own
 * actions: Tag… opens the form's tag row, a press re-reads a clean form),
 * standing in for the read view's. Save and Cancel are at the pane's foot
 * (ItemFormPaneFooter).
 */
export default function ItemFormHeader({
	paneSlot = undefined,
	titleSingular,
	cardVisual,
	title,
	item,
	isNewRecord,
	templateId,
	isProfileLaunch,
	quickAddName,
	itemActions,
	draftStatus,
	refreshing,
	onRefresh,
	itemLoading,
	actionButtons,
	formActions,
	itemGone,
}) {
	if (paneSlot !== undefined) {
		if (!paneSlot) return null;
		return createPortal(
			<>
				{actionButtons}
				{itemActions.menu ? (
					<Dropdown trigger={['click']} placement="bottomRight" menu={itemActions.menu}>
						<Button icon={<KEBAB />} aria-label="Item actions" />
					</Dropdown>
				) : null}
			</>,
			paneSlot,
		);
	}
	return (
		<ContextBar
			className="context-bar--in-shell"
			eyebrow={titleSingular}
			// The shell above names the tab and the h1; the item's title is
			// this bar's h2.
			titleAs="h2"
			nameTab={false}
			record={cardVisual}
			kind="item"
			title={title}
			below={(item?.intake || item?.copiedFrom || isDemoItem(item) || (isNewRecord && templateId)) ? (
				<>
					{/* A copy made with Copy to… (KPR-1024). */}
					<CopiedFromNote item={item} />
					{/* A submission nobody has accepted says so on its own form
					    too: a triager may fix a typo here before accepting it
					    from the list. */}
					<IntakePendingTag item={item} />
					<DemoTag item={item} />
					<ItemSourceLine intake={item?.intake} />
					{isNewRecord && templateId ? (
						<span className="item-form-quick-add" data-testid="quick-add-line">
							{isProfileLaunch ? <DUPLICATE aria-hidden="true" /> : <LIGHTNING aria-hidden="true" />}
							{' '}{isProfileLaunch ? 'Duplicate' : 'Quick add'}{quickAddName ? ` · ${quickAddName}` : ''}
						</span>
					) : null}
				</>
			) : null}
			// The ⋯ is the bar's (one kebab): the item's actions ride in as
			// menuActions, beside the Help entry the bar adds.
			menuActions={itemActions.actions}
			menuLabel="Item actions"
			actions={(
				<>
					{isNewRecord && draftStatus === 'saved' ? (
						<span className="item-form-draft">Draft saved</span>
					) : null}
					{isNewRecord && draftStatus === 'failed' ? (
						<span className="item-form-draft">Draft not saved</span>
					) : null}
					{/* Refresh leads the header's actions, a square glyph before
					    the labeled ones. An existing item only: a new one has
					    nothing to read again. */}
					{isNewRecord ? null : (
						<RefreshButton pending={refreshing} onRefresh={onRefresh} disabled={itemLoading} />
					)}
					{actionButtons}
					{itemGone ? null : formActions}
				</>
			)}
		/>
	);
}

/**
 * Cancel · Save at the foot of the detail pane, where the read view had Edit
 * (KPR-1644). A form that became read-only while open (the record locked under
 * it) keeps a way back: Cancel alone. The keyboard hint says the form's keys.
 */
export function ItemFormPaneFooter({ slot, formActions, onCancel }) {
	if (!slot) return null;
	return createPortal(
		<>
			<div className="item-pane__footer-actions">
				{/* Outside the form's antd `disabled` context, as FormActionsFooter. */}
				<ConfigProvider componentDisabled={false}>
					{formActions || <Button onClick={onCancel}>Cancel</Button>}
				</ConfigProvider>
			</div>
			<span className="item-pane__hint" aria-hidden="true">⌘↵ save · Esc cancel</span>
		</>,
		slot,
	);
}
